import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";
import { buildPublicEventUrl } from "../../utils/publicEventUrl";
import { arrayFromResponse } from "../../utils/arrayResponse";
import { Context } from "../../Reducers/Store";

function Tooltip({ label, children }) {
  return (
    <div className="relative group/tip inline-flex">
      {children}
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap rounded-lg bg-slate-800 px-2.5 py-1 text-xs font-medium text-white opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 z-20 shadow-sm">
        {label}
        <span className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
      </span>
    </div>
  );
}

const FILTROS = [
  { key: "ativos", label: "Ativos" },
  { key: "inativos", label: "Inativos" },
  { key: "todos", label: "Todos" },
];

export default function EventoList() {
  const navigate = useNavigate();
  const [state] = useContext(Context);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("ativos");
  const podeCriarEventos = !!state?.profile?.podeCriarEventos;

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API}/evento/meus-eventos`)
      .then((res) => setEventos(arrayFromResponse(res.data)))
      .catch((err) => {
        console.error(err);
        AlertComponent("Erro", "Erro ao carregar eventos", { icon: "error" });
      })
      .finally(() => setLoading(false));
  }, []);

  const formatarData = (data) =>
    new Date(data).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Sao_Paulo",
    });

  const handleToggleAtivo = async (e, evento) => {
    e.stopPropagation();
    if (!evento.acesso?.podeEditar) return;
    try {
      const res = await axios.patch(`${process.env.REACT_APP_API}/evento/${evento.id}/toggle-ativo`);
      setEventos((prev) =>
        prev.map((ev) => ev.id === evento.id ? { ...ev, isActive: res.data.isActive } : ev)
      );
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", "Erro ao alterar status do evento", { icon: "error" });
    }
  };

  const handleDeletar = async (e, evento) => {
    e.stopPropagation();
    if (!evento.acesso?.podeExcluir) return;
    const confirmado = await AlertComponent(
      "Excluir evento",
      `Tem certeza que deseja excluir "${evento.nome}"? Esta ação não pode ser desfeita.`,
      {
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Excluir",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#dc2626",
      },
      null,
      false
    );
    if (!confirmado?.isConfirmed) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API}/evento/${evento.id}`);
      setEventos((prev) => prev.filter((ev) => ev.id !== evento.id));
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", "Erro ao excluir evento", { icon: "error" });
    }
  };

  const eventosFiltrados = eventos.filter((e) => {
    if (filtro === "ativos") return e.isActive;
    if (filtro === "inativos") return !e.isActive;
    return true;
  });

  const contagem = {
    ativos: eventos.filter((e) => e.isActive).length,
    inativos: eventos.filter((e) => !e.isActive).length,
    todos: eventos.length,
  };

  return (
    <div className="max-w-5xl mx-auto">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Meus Eventos</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {loading ? "Carregando..." : `${contagem.todos} evento${contagem.todos !== 1 ? "s" : ""} cadastrado${contagem.todos !== 1 ? "s" : ""}`}
          </p>
        </div>
        {podeCriarEventos && (
          <button
            onClick={() => navigate("/eventos/novo")}
            className="self-start sm:self-auto flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl text-white transition shadow-sm"
            style={{ background: "linear-gradient(135deg,#8b5cf6,#6d28d9)" }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            Novo Evento
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 mb-5 bg-slate-100 p-1 rounded-xl w-fit">
        {FILTROS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFiltro(f.key)}
            className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg transition-all ${
              filtro === f.key
                ? "bg-white text-slate-800 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {f.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
              filtro === f.key ? "bg-violet-100 text-violet-700" : "bg-slate-200 text-slate-500"
            }`}>
              {loading ? "—" : contagem[f.key]}
            </span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
          </div>
        ) : eventosFiltrados.length === 0 ? (
          <div className="text-center py-20 px-6">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-slate-600 font-semibold mb-1">Nenhum evento encontrado</p>
            <p className="text-slate-400 text-sm mb-5">
              {filtro === "ativos" ? "Você não tem eventos ativos no momento." : "Nenhum resultado para este filtro."}
            </p>
            {podeCriarEventos && (
              <button
                onClick={() => navigate("/eventos/novo")}
                className="px-5 py-2 text-sm font-semibold rounded-xl text-white transition"
                style={{ background: "linear-gradient(135deg,#8b5cf6,#6d28d9)" }}
              >
                Criar evento
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Cards — mobile */}
            <div className="md:hidden divide-y divide-slate-100">
              {eventosFiltrados.map((evento) => (
                <div
                  key={evento.id}
                  onClick={() => navigate(`/eventos/${evento.id}`)}
                  className="relative flex items-start gap-4 p-4 hover:bg-slate-50 cursor-pointer transition-colors group"
                >
                  {/* Status bar */}
                  <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-none ${evento.isActive ? "bg-emerald-400" : "bg-slate-200"}`} />

                  {/* Date box */}
                  <div className="w-12 shrink-0 text-center">
                    <div
                      className="rounded-xl py-1.5 px-1"
                      style={{ background: "linear-gradient(135deg,#ede9fe,#ddd6fe)" }}
                    >
                      <p className="text-lg font-extrabold text-violet-700 leading-none">
                        {new Date(evento.dataDoEvento).toLocaleDateString("pt-BR", { day: "2-digit", timeZone: "America/Sao_Paulo" })}
                      </p>
                      <p className="text-xs font-medium text-violet-500">
                        {new Date(evento.dataDoEvento).toLocaleDateString("pt-BR", { month: "short", timeZone: "America/Sao_Paulo" }).replace(".", "")}
                      </p>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="font-semibold text-slate-800 truncate group-hover:text-violet-700 transition-colors">{evento.nome}</p>
                      {evento.exigirCodigoConvite && (
                        <span className="shrink-0 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-slate-100 text-slate-600">
                          Fechado
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate mt-0.5">{evento.localizacao}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {new Date(evento.dataDoEvento).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" })}
                    </p>
                  </div>

                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                      evento.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                    }`}>
                      {evento.isActive ? "Ativo" : "Inativo"}
                    </span>
                    <div className="flex items-center gap-1">
                      <Tooltip label="Editar">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/eventos/${evento.id}`); }}
                          className="p-1.5 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                      </Tooltip>
                      <Tooltip label={(evento.slug || evento.urlExterna) ? "Ver página pública" : "Sem link público definido"}>
                        <button
                          onClick={(e) => { e.stopPropagation(); window.open(buildPublicEventUrl(evento), "_blank"); }}
                          disabled={!evento.slug && !evento.urlExterna}
                          className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </button>
                      </Tooltip>
                      <Tooltip label={evento.isActive ? "Desativar evento" : "Ativar evento"}>
                        <button
                          onClick={(e) => handleToggleAtivo(e, evento)}
                          disabled={!evento.acesso?.podeEditar}
                          className={`p-1.5 rounded-lg transition-colors ${evento.isActive ? "text-slate-400 hover:text-amber-600 hover:bg-amber-50" : "text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {evento.isActive
                              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            }
                          </svg>
                        </button>
                      </Tooltip>
                      <Tooltip label="Lista de confirmações">
                        <button
                          onClick={(e) => { e.stopPropagation(); window.open(`/confirmacoes/evento/${evento.id}`, "_blank"); }}
                          className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                        </button>
                      </Tooltip>
                      <Tooltip label="Convidados">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/eventos/${evento.id}/convidados`); }}
                          className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m10-4.13a3 3 0 11-6 0 3 3 0 016 0zM9 10a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </button>
                      </Tooltip>
                      {evento.acesso?.podeExcluir && (
                        <Tooltip label="Excluir evento">
                          <button
                            onClick={(e) => handleDeletar(e, evento)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Tabela — desktop */}
            <div className="hidden md:block">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Evento</th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Data</th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Local</th>
                    <th className="px-4 py-3.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider w-28">Status</th>
                    <th className="px-4 py-3.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider w-28">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {eventosFiltrados.map((evento) => (
                    <tr
                      key={evento.id}
                      className="hover:bg-slate-50 cursor-pointer transition-colors group"
                      onClick={() => navigate(`/eventos/${evento.id}`)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full shrink-0 ${evento.isActive ? "bg-emerald-400" : "bg-slate-300"}`} />
                          <span className="font-semibold text-slate-800 group-hover:text-violet-700 transition-colors">
                            {evento.nome}
                          </span>
                          {evento.exigirCodigoConvite && (
                            <span className="shrink-0 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-slate-100 text-slate-600">
                              Fechado
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap">{formatarData(evento.dataDoEvento)}</td>
                      <td className="px-4 py-4 text-sm text-slate-600 max-w-[200px] truncate">{evento.localizacao}</td>
                      <td className="px-4 py-4 text-center">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                          evento.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                        }`}>
                          {evento.isActive ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Tooltip label="Editar">
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/eventos/${evento.id}`); }}
                              className="p-2 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                          </Tooltip>
                          <Tooltip label={(evento.slug || evento.urlExterna) ? "Ver página pública" : "Sem link público definido"}>
                            <button
                              onClick={(e) => { e.stopPropagation(); window.open(buildPublicEventUrl(evento), "_blank"); }}
                              disabled={!evento.slug && !evento.urlExterna}
                              className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </button>
                          </Tooltip>
                          <Tooltip label={evento.isActive ? "Desativar evento" : "Ativar evento"}>
                            <button
                              onClick={(e) => handleToggleAtivo(e, evento)}
                              disabled={!evento.acesso?.podeEditar}
                              className={`p-2 rounded-lg transition-colors ${evento.isActive ? "text-slate-400 hover:text-amber-600 hover:bg-amber-50" : "text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {evento.isActive
                                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                }
                              </svg>
                            </button>
                          </Tooltip>
                          <Tooltip label="Lista de confirmações">
                            <button
                              onClick={(e) => { e.stopPropagation(); window.open(`/confirmacoes/evento/${evento.id}`, "_blank"); }}
                              className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                              </svg>
                            </button>
                          </Tooltip>
                          <Tooltip label="Convidados">
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/eventos/${evento.id}/convidados`); }}
                              className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m10-4.13a3 3 0 11-6 0 3 3 0 016 0zM9 10a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </button>
                          </Tooltip>
                          {evento.acesso?.podeExcluir && (
                            <Tooltip label="Excluir evento">
                              <button
                                onClick={(e) => handleDeletar(e, evento)}
                                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
